/**
 * Analytics Types
 *
 * Type definitions for analytics tracking events and parameters.
 * These types ensure type-safe event tracking throughout the application.
 */

/**
 * Event categories for organizing analytics events
 */
export const EventCategory = {
  PAGE_VIEW: 'page_view',
  WALLET: 'wallet',
  TRANSACTION: 'transaction',
  TOKEN: 'token',
  DAO: 'dao',
  UI_INTERACTION: 'ui_interaction',
  ERROR: 'error',
  FORM: 'form',
} as const;

export type EventCategoryType = typeof EventCategory[keyof typeof EventCategory];

/**
 * Common event actions for each category
 */
export const EventAction = {
  // Wallet actions
  WALLET_CONNECT: 'wallet_connect',
  WALLET_DISCONNECT: 'wallet_disconnect',
  WALLET_CHANGE: 'wallet_change',

  // Transaction actions
  TRANSACTION_INITIATE: 'transaction_initiate',
  TRANSACTION_SUBMIT: 'transaction_submit',
  TRANSACTION_SUCCESS: 'transaction_success',
  TRANSACTION_FAILED: 'transaction_failed',

  // Token actions
  FAUCET_REQUEST: 'faucet_request',
  FAUCET_SUCCESS: 'faucet_success',
  FAUCET_FAILED: 'faucet_failed',
  TOKEN_TRANSFER: 'token_transfer',

  // DAO actions
  DAO_JOIN: 'dao_join',
  DAO_LEAVE: 'dao_leave',
  PROJECT_CREATE: 'project_create',
  PROJECT_VIEW: 'project_view',

  // Account actions
  ACCOUNT_LINKED: 'account_linked',
  ACCOUNT_UNLINKED: 'account_unlinked',

  // UI interactions
  BUTTON_CLICK: 'button_click',
  LINK_CLICK: 'link_click',
  MODAL_OPEN: 'modal_open',
  MODAL_CLOSE: 'modal_close',

  // Form actions
  FORM_SUBMIT: 'form_submit',
  FORM_ERROR: 'form_error',

  // Error actions
  API_ERROR: 'api_error',
  RUNTIME_ERROR: 'runtime_error',
} as const;

export type EventActionType = typeof EventAction[keyof typeof EventAction];

/**
 * Base interface for all analytics events
 */
export interface BaseEventParams {
  /** Optional label for additional context */
  label?: string;
  /** Optional numeric value associated with the event */
  value?: number;
  /** Any additional custom parameters */
  [key: string]: any;
}

/**
 * Wallet event parameters
 */
export interface WalletEventParams extends BaseEventParams {
  wallet_provider?: 'phantom' | 'solflare' | 'backpack' | 'other';
  wallet_address?: string;
  connection_method?: 'desktop' | 'mobile' | 'deeplink';
}

/**
 * Transaction event parameters
 */
export interface TransactionEventParams extends BaseEventParams {
  transaction_type?: string;
  transaction_signature?: string;
  amount?: number;
  token?: string;
  status?: 'pending' | 'success' | 'failed';
  error_message?: string;
}

/**
 * Token event parameters
 */
export interface TokenEventParams extends BaseEventParams {
  token_symbol?: string;
  token_amount?: number;
  token_mint?: string;
  recipient?: string;
}

/**
 * DAO event parameters
 */
export interface DaoEventParams extends BaseEventParams {
  dao_id?: string;
  dao_name?: string;
  project_id?: string;
  project_name?: string;
}

/**
 * UI interaction event parameters
 */
export interface UiEventParams extends BaseEventParams {
  component?: string;
  location?: string;
  element_id?: string;
}

/**
 * Form event parameters
 */
export interface FormEventParams extends BaseEventParams {
  form_name?: string;
  field_name?: string;
  error_type?: string;
}

/**
 * Error event parameters
 */
export interface ErrorEventParams extends BaseEventParams {
  error_message?: string;
  error_code?: string | number;
  error_stack?: string;
  endpoint?: string;
  status_code?: number;
}

/**
 * Page view parameters
 */
export interface PageViewParams {
  page_title?: string;
  page_location?: string;
  page_path?: string;
  [key: string]: any;
}

/**
 * User properties for identification
 */
export interface UserProperties {
  user_id?: string;
  wallet_address?: string;
  [key: string]: any;
}
